// @ts-nocheck
import React, { useMemo, useState } from 'react';
const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
import {
	Card,
	CardBody,
	Stack,
	Heading,
	Text,
	Image,
	Divider,
	CardFooter,
	Button,
	Badge,
	Box,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	HStack,
	ModalFooter,
	VStack,
	Wrap,
	WrapItem,
	Flex,
	IconButton,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';

const ProductPreviewCard = ({
	title,
	photo,
	price,
	description,
	attribute = [],
	attributes,
	variants = [],
	unit,
	quantityPerUnit,
	image,
	prev,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [quantity, setQuantity] = useState(1);
	const [selectedPerType, setSelectedPerType] = useState({});

	const attrs = attributes?.length ? attributes : attribute;
	const hasVariants = Array.isArray(variants) && variants.length > 0;

	const enrichedVariants = useMemo(() => {
		if (!hasVariants) return [];
		return variants.map((v, vIdx) => {
			const variantAttrs = (v.attrIndices || []).map((idx, i) => {
				const a = attrs[idx];
				if (!a) return null;
				return {
					attribute_id: `${a.attribute_name}:${a.attribute_value}:${idx}`,
					attribute_name: a.attribute_name,
					attribute_value: a.attribute_value,
				};
			}).filter(Boolean);
			return {
				...v,
				key: v.variant_id || vIdx,
				final_price: Number(v.price) || 0,
				attributes: v.attributes?.length ? v.attributes : variantAttrs,
			};
		});
	}, [variants, attrs, hasVariants]);

	const availableVariants = enrichedVariants.filter((v) => v.final_price > 0);
	const listingPrice = useMemo(() => {
		if (hasVariants) {
			if (availableVariants.length > 0) {
				return Math.min(...availableVariants.map((v) => v.final_price));
			}
			return 0;
		}
		return Number(price) || 0;
	}, [hasVariants, availableVariants, price]);

	const attrTypes = availableVariants.length > 0
		? [...new Set(availableVariants.flatMap((v) => v.attributes.map((a) => a.attribute_name)))]
		: [];

	const allSelected = attrTypes.length > 0 && attrTypes.every((t) => selectedPerType[t] != null);

	const matchedVariant = allSelected
		? availableVariants.find((v) =>
			attrTypes.every((t) =>
				v.attributes.find((a) => a.attribute_name === t && a.attribute_id === selectedPerType[t])
			)
		)
		: null;

	const displayPrice = matchedVariant ? Number(matchedVariant.final_price) : listingPrice;
	const needsSelection = hasVariants && !allSelected;

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setQuantity(1);
		setSelectedPerType({});
	};
	const handleOpenModal = (e) => {
		e?.stopPropagation?.();
		setIsModalOpen(true);
	};

	const imgSrc = photo
		? photo
		: image
			? `${BASE_URL}/api/image?filename=${image}`
			: 'https://via.placeholder.com/300?text=No+Image';

	return (
		<>
			<Card
				maxW="sm"
				position="relative"
				onClick={handleOpenModal}
				cursor="pointer"
				borderRadius="2xl"
				overflow="hidden"
				boxShadow="sm"
				border="1px solid"
				borderColor="gray.100"
				bg="white"
				transition="all 0.2s"
				_hover={{ boxShadow: 'lg', transform: 'translateY(-3px)', borderColor: 'gray.200' }}
			>
				{hasVariants && listingPrice > 0 && (
					<Badge position="absolute" top={3} left={3} colorScheme="purple" borderRadius="md" px={2} py={0.5} fontSize="xs" zIndex={1}>
						Options
					</Badge>
				)}

				<Box
					position="relative"
					width="100%"
					height="200px"
					bg="gray.50"
					display="flex"
					justifyContent="center"
					alignItems="center"
				>
					<Image
						src={imgSrc}
						maxH="170px"
						maxW="85%"
						objectFit="contain"
						alt={title || 'Product photo'}
						fallbackSrc="https://via.placeholder.com/300?text=No+Image"
					/>
				</Box>

				<CardBody pb={2} pt={3}>
					<Heading size="sm" noOfLines={2} color="gray.800" mb={1}>
						{title || 'Product Title'}
					</Heading>
					{unit && quantityPerUnit && (
						<Text fontSize="xs" color="gray.500" mb={1}>{quantityPerUnit} per {unit}</Text>
					)}
					{listingPrice > 0 ? (
						hasVariants ? (
							<HStack spacing={2} align="baseline">
								<Text fontSize="lg" fontWeight="bold" color="blue.600">From ${listingPrice.toFixed(2)}</Text>
								<Badge colorScheme="purple" fontSize="0.65rem">Options</Badge>
							</HStack>
						) : (
							<Text fontSize="lg" fontWeight="bold" color="blue.600">${listingPrice.toFixed(2)}</Text>
						)
					) : (
						<Text fontSize="sm" fontWeight="semibold" color="gray.400">
							{hasVariants ? 'Set variant prices to preview' : 'Enter a price'}
						</Text>
					)}
				</CardBody>

				<CardFooter pt={0} pb={3} px={4}>
					{hasVariants ? (
						<Button w="full" size="sm" colorScheme="blue" borderRadius="lg" fontWeight="semibold" onClick={handleOpenModal}>
							Select Options
						</Button>
					) : (
						<Button w="full" size="sm" colorScheme="blue" borderRadius="lg" fontWeight="semibold" isDisabled={!listingPrice}>
							Add to Cart
						</Button>
					)}
				</CardFooter>
			</Card>

			<Modal isOpen={isModalOpen} onClose={handleCloseModal} size="lg" isCentered>
				<ModalOverlay bg="blackAlpha.600" />
				<ModalContent borderRadius="2xl" overflow="hidden">
					<ModalCloseButton zIndex={10} />
					<Box bg="gray.50" h="240px" display="flex" alignItems="center" justifyContent="center">
						<Image src={imgSrc} alt={title} maxH="200px" maxW="70%" objectFit="contain" fallbackSrc="https://via.placeholder.com/600x400?text=No+Image" />
					</Box>
					<ModalHeader pb={1} pt={4}>{title || 'Product Title'}</ModalHeader>
					<ModalBody pt={0}>
						<VStack spacing={3} align="stretch">
							<Text fontSize="sm" color="gray.600">{description || 'No description available.'}</Text>
							{unit && quantityPerUnit && (
								<HStack spacing={4} fontSize="sm" color="gray.500">
									<Text>Sold in <strong>{unit}s</strong></Text>
									<Text><strong>{quantityPerUnit}</strong> per {unit}</Text>
								</HStack>
							)}
							<Divider />

							{hasVariants && availableVariants.length === 0 && (
								<Text fontSize="sm" color="gray.500">Set variant base prices to preview options.</Text>
							)}

							{attrTypes.map((typeName) => {
								const options = availableVariants.reduce((acc, v) => {
									const a = v.attributes.find((x) => x.attribute_name === typeName);
									if (a && !acc.find((x) => x.attribute_id === a.attribute_id)) acc.push(a);
									return acc;
								}, []);
								return (
									<Box key={typeName}>
										<Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
											{typeName}
										</Text>
										<Wrap spacing={2}>
											{options.map((opt) => (
												<WrapItem key={opt.attribute_id}>
													<Button
														size="sm"
														borderRadius="lg"
														variant={selectedPerType[typeName] === opt.attribute_id ? 'solid' : 'outline'}
														colorScheme={selectedPerType[typeName] === opt.attribute_id ? 'blue' : 'gray'}
														onClick={() => setSelectedPerType((prev) => ({ ...prev, [typeName]: opt.attribute_id }))}
													>
														{opt.attribute_value}
													</Button>
												</WrapItem>
											))}
										</Wrap>
									</Box>
								);
							})}

							{hasVariants && availableVariants.length > 0 && !allSelected && (
								<Text fontSize="xs" color="orange.500">Please select all options before adding to cart.</Text>
							)}

							<Flex justify="space-between" align="center">
								<Box>
									<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
										{hasVariants && !matchedVariant ? 'Starting at' : 'Price per unit'}
									</Text>
									{displayPrice > 0 ? (
										<Text fontSize="2xl" fontWeight="bold" color="blue.600">${displayPrice.toFixed(2)}</Text>
									) : (
										<Text fontSize="lg" fontWeight="semibold" color="gray.400">Price unavailable</Text>
									)}
								</Box>
								<Badge colorScheme="green">In Stock</Badge>
							</Flex>

							{displayPrice > 0 && (
								<>
									<Flex justify="space-between" align="center">
										<Text fontSize="sm" fontWeight="medium" color="gray.600">Quantity</Text>
										<HStack spacing={0} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
											<IconButton icon={<MinusIcon boxSize={2.5} />} aria-label="Decrease" size="sm" variant="ghost" onClick={() => setQuantity((q) => Math.max(1, q - 1))} isDisabled={quantity <= 1} h={9} w={9} />
											<Text px={4} fontSize="md" fontWeight="semibold">{quantity}</Text>
											<IconButton icon={<AddIcon boxSize={2.5} />} aria-label="Increase" size="sm" variant="ghost" onClick={() => setQuantity((q) => q + 1)} h={9} w={9} />
										</HStack>
									</Flex>
									<Flex justify="space-between" align="center" bg="blue.50" px={4} py={2} borderRadius="lg">
										<Text fontSize="sm" color="blue.600">Total</Text>
										<Text fontSize="lg" fontWeight="bold" color="blue.700">${(displayPrice * quantity).toFixed(2)}</Text>
									</Flex>
								</>
							)}
						</VStack>
					</ModalBody>
					<ModalFooter pt={2} gap={2}>
						<Button
							w="full"
							colorScheme="blue"
							borderRadius="xl"
							size="lg"
							fontWeight="bold"
							isDisabled={!displayPrice || needsSelection}
						>
							{needsSelection
								? 'Select Options'
								: `Add ${quantity > 1 ? `${quantity} ` : ''}to Cart — $${(displayPrice * quantity).toFixed(2)}`}
						</Button>
						<Button onClick={handleCloseModal} size="lg" variant="ghost">Close</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default ProductPreviewCard;
